import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
    IframeHTMLAttributes,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

interface ICertificatePreviewModalProps {
    iframeRef?: RefObject<HTMLIFrameElement | null>;
    iframeProps?: IframeHTMLAttributes<HTMLIFrameElement>;
    onClose: () => void;
    open: boolean;
}

const CERTIFICATE_ASPECT_RATIO = 1.41428571429;
// A4 landscape at 96 DPI: 1123px × 794px
const CERTIFICATE_INTRINSIC_WIDTH = 1123;

export const CertificatePreviewModal = ({
    iframeRef,
    iframeProps,
    onClose,
    open,
}: ICertificatePreviewModalProps) => {
    const dialogContentRef = useRef<HTMLDivElement | null>(null);
    const [isDialogContentLoaded, setIsDialogContentLoaded] = useState(false);
    const [scale, setScale] = useState<number>(1);

    // Callback ref to detect when DialogContent is mounted
    const dialogContentCallbackRef = useCallback(
        (node: HTMLDivElement | null) => {
            dialogContentRef.current = node;
            setIsDialogContentLoaded(!!node);
        },
        []
    );

    const updateScale = useCallback(() => {
        const dialogContentWidth =
            dialogContentRef.current?.getBoundingClientRect().width;

        if (!dialogContentWidth) return;

        // Calculate scale based on DialogContent width vs certificate intrinsic width
        const newScale = dialogContentWidth / CERTIFICATE_INTRINSIC_WIDTH;
        setScale(newScale);
    }, []);

    // Update scale when dialog opens and refs are loaded
    useEffect(() => {
        if (!open || !isDialogContentLoaded) return;

        // Small delay to ensure layout is complete
        const timeoutId = setTimeout(() => {
            updateScale();
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [open, isDialogContentLoaded, updateScale]);

    // Handle window resize and DialogContent size changes
    useEffect(() => {
        if (!open || !isDialogContentLoaded || !dialogContentRef.current) {
            return;
        }

        const handleResize = () => {
            updateScale();
        };

        // Use ResizeObserver to detect DialogContent size changes
        const resizeObserver = new ResizeObserver(() => {
            updateScale();
        });

        resizeObserver.observe(dialogContentRef.current);
        window.addEventListener('resize', handleResize);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [open, isDialogContentLoaded, updateScale]);

    // Handle iframe load event to get actual content dimensions if available
    const handleIframeLoad = useCallback(() => {
        // Try to get dimensions from iframe content if accessible
        try {
            const iframe = iframeRef?.current;

            if (iframe?.contentWindow?.document?.body) {
                const body = iframe.contentWindow.document.body;
                const contentWidth = body.scrollWidth || body.offsetWidth;

                if (contentWidth && contentWidth > 0) {
                    // Use actual content width if available
                    const dialogContentWidth =
                        dialogContentRef.current?.getBoundingClientRect().width;

                    if (dialogContentWidth) {
                        setScale(dialogContentWidth / contentWidth);

                        return;
                    }
                }
            }
        } catch {
            // Cross-origin or other error, fall back to intrinsic width
        }

        updateScale();
    }, [iframeRef, updateScale]);

    // Reset loaded state when dialog closes
    useEffect(() => {
        if (!open) {
            setIsDialogContentLoaded(false);
        }
    }, [open]);

    return (
        <Dialog maxWidth={false} open={open} onClose={onClose}>
            <DialogContent
                ref={dialogContentCallbackRef}
                sx={{
                    width: `min(90vw, calc(70vh * ${CERTIFICATE_ASPECT_RATIO}))`,
                    aspectRatio: CERTIFICATE_ASPECT_RATIO,
                    p: 0,
                    maxWidth: '1000px',
                    overflow: 'hidden',
                }}
            >
                <iframe
                    {...iframeProps}
                    ref={iframeRef}
                    style={{
                        border: 'none',
                        width: `${CERTIFICATE_INTRINSIC_WIDTH}px`,
                        height: `${CERTIFICATE_INTRINSIC_WIDTH / CERTIFICATE_ASPECT_RATIO}px`,
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                    }}
                    onLoad={handleIframeLoad}
                />
            </DialogContent>
        </Dialog>
    );
};
